// House / lofi house (docs/AUDIO_DESIGN.md §12): deep house through a dusty tape (DJ Seinfeld, Ross From Friends). A round
// four-on-the-floor kick, a clap on 2 and 4 and open hats on the off-beats; deep m9 / m11 / maj9 / 7sus4 chords stabbed on
// the off-beats by a drawbar organ through a slowly sweeping low-pass and a sidechain pump; a rolling off-beat sub; a vocal
// chop or a soft pluck on some tracks. The most upbeat style (108-124 BPM), still warm and low-passed.
import { bassNote, voice as rootless } from '../harmony.js';
import { createPump, createHouseChords, createHouseBass, createHouseKit, createHouseLead } from './house-voices.js';

const mod12 = n => ((n % 12) + 12) % 12;
const ENERGY = { chill: 0, balanced: 1, upbeat: 2 };

/** Bass cells, one letter a 16th, never on the kick's steps 0 4 8 12: R root · o octave · 5 fifth · . rest */
const BASS_CELLS = {
  lazy:   '..R.......R.....',
  off:    '..R...R...R...R.',
  oct:    '..R...o...R...o.',
  roll:   '..Ro..R...Ro..R.',
  sync:   '..R..R.o..R..R..',
  gallop: '..RR..o...RR..o.',
};
//                 per energy: weights of the track's main cell (the second one is drawn from the same table)
const BASS_POOL = [
  { lazy: 1.2, off: 1, oct: 0.6 },
  { off: 1, oct: 1, roll: 0.6, sync: 0.5, lazy: 0.3 },
  { oct: 1, roll: 1, sync: 0.8, gallop: 0.6, off: 0.5 },
];
const pickW = (r, w) => {
  let u = r() * Object.values(w).reduce((a, b) => a + b, 0);
  for (const [k, v] of Object.entries(w)) if ((u -= v) < 0) return k;
  return Object.keys(w)[0];
};

/** Voicings: interval shapes over the root (any octave), 4 notes, only the chord's own tones; the first shape of each is
 *  the "deep" one (4ths and 5ths stacked, the So What chord for m11). Other qualities use jazzhop's rootless voicing. */
const SHAPES = {
  m7:      [[10, 15, 19, 24], [3, 7, 10, 15]],                    // ♭7 ♭3 5 1 · ♭3 5 ♭7 ♭3
  m9:      [[10, 15, 19, 26], [3, 7, 10, 14]],                    // ♭7 ♭3 5 9 · ♭3 5 ♭7 9
  m11:     [[5, 10, 15, 19], [3, 10, 14, 17]],                    // 11 ♭7 ♭3 5 · ♭3 ♭7 9 11
  maj7:    [[11, 16, 19, 24], [4, 7, 11, 16]],                    // 7 3 5 1 · 3 5 7 3
  maj9:    [[11, 16, 19, 26], [4, 7, 11, 14]],                    // 7 3 5 9 · 3 5 7 9
  '7sus4': [[10, 17, 19, 24], [5, 10, 12, 17]],                   // ♭7 4 5 1 · 4 ♭7 1 4
  '9sus4': [[5, 10, 14, 19], [10, 14, 17, 19]],                   // 4 ♭7 9 5 · ♭7 9 4 5
  add9:    [[4, 7, 12, 14], [7, 12, 14, 16]],                     // 3 5 1 9 · 5 1 9 3
  '69':    [[4, 9, 14, 19], [9, 14, 16, 19]],                     // 3 6 9 5 · 6 9 3 5
};
const mean = a => a.reduce((s, v) => s + v, 0) / a.length;

export default {
  id: 'house', name: 'House', desc: 'Deep-house chords over a four-on-the-floor kick, dusty and warm',
  //            chill                                   balanced                                  upbeat
  vary: 'colour',
  energy: {
    stretch:  [0.45,                                    0.3,                                      0.2],
    bpm:      [[108, 114],                              [114, 120],                               [119, 124]],
    swing:    [[51, 54],                                [50, 53],                                 [50, 52.5]],
    snareLag: [[0.003, 0.009],                          [0.002, 0.007],                           [0.001, 0.005]],
    groove:   [[{ deep: 1.2, soul: 1, ride: 0.4 }, { deep: 1.2, soul: 1, ride: 0.4 }],
               [{ deep: 0.8, soul: 0.8, shaker: 1, ride: 0.6 }, { deep: 0.8, soul: 0.8, shaker: 1, ride: 0.6 }],
               [{ shaker: 1.2, drive: 1, ride: 0.8, soul: 0.5 }, { shaker: 1.2, drive: 1, ride: 0.8, soul: 0.5 }]],
    drumLevel:[0.85,                                    0.95,                                     1],
    kitLp:    [[5000, 6800],                            [5800, 8000],                             [6500, 9000]],
    epLp:     [[1100, 1600],                            [1400, 2100],                             [1700, 2600]],   // chord LP in A (swept per section)
    strum:    [[0.002, 0.008],                          [0.001, 0.006],                           [0.001, 0.004]],
    epVel:    [[0.5, 0.6],                              [0.54, 0.65],                             [0.58, 0.7]],
    comp:     [{ pad: [0.8, 1.4], breath: [0.5, 1], sparse: [0.2, 0.6], tres: [0.05, 0.3], stab: [0, 0.2], off: [0, 0.15] },
               { pad: [0.2, 0.6], breath: [0.3, 0.7], sparse: [0.3, 0.8], tres: [0.3, 0.8], stab: [0.3, 0.8], off: [0.2, 0.6] },
               { pad: [0, 0.2], breath: [0.1, 0.4], sparse: [0.2, 0.5], tres: [0.4, 0.9], stab: [0.6, 1.1], off: [0.5, 1] }],
    push:     [0.1,                                     0.2,                                      0.3],
    legato:   [0.7,                                     0.5,                                      0.35],
    lead:     [0.4,                                     0.5,                                      0.6],
    softLead: [0.6,                                     0.55,                                     0.5],            // 'soft' plays the vox, else the pluck
    tone:     [[4800, 6800],                            [5800, 8200],                             [6800, 9200]],
    wowCents: [[5, 10],                                 [4, 8],                                   [3, 7]],
    dust:     [[0.9, 1.5],                              [0.7, 1.3],                               [0.6, 1.1]],
    introHats:[0.45,                                    0.6,                                      0.75],
    leadFrom: [2,                                       2,                                        1],
    hatsFirst:[0.3,                                     0.2,                                      0.12],
    breakDrums:[0.35,                                   0.55,                                     0.75],
    breakLead:[0.2,                                     0.3,                                      0.4],
    // development (arrange.js BY_ENERGY): the groove first, as house does; a short solo now and then
    solo:       [0.15, 0.2, 0.25],
    intro:      [{ keys: 0.2, beat: 0.35, groove: 0.3, lead: 0.05, cold: 0.1 }, { keys: 0.15, beat: 0.4, groove: 0.3, lead: 0.05, cold: 0.1 }, { keys: 0.1, beat: 0.4, groove: 0.35, lead: 0, cold: 0.15 }],
  },
  progressions: {
    major: {                                                                                     // centred on ii and vi
      H1: [[2, 'm9', 4], [7, '7sus4', 4], [0, 'maj9', 8]],                                       // ii9 V7sus4 Imaj9
      H2: [[9, 'm9', 8], [5, 'maj9', 8]],                                                        // vi9 IVmaj9
      H3: [[2, 'm11', 8], [0, 'maj9', 8]],                                                       // ii11 Imaj9
      H4: [[9, 'm9', 4], [7, 'add9', 4], [5, 'maj9', 8]],                                        // vi9 Vadd9 IVmaj9
      H5: [[9, 'm11', 8], [2, 'm9', 4], [7, '9sus4', 4]],                                        // vi11 ii9 V9sus4
      H6: [[9, 'm7', 4], [5, 'maj7', 4], [7, 'add9', 4], [9, 'm9', 4]],                          // vi7 IVmaj7 V vi9
    },
    minor: {
      h1: [[0, 'm9', 8], [5, 'm9', 8]],                                                          // i9 iv9
      h2: [[0, 'm7', 4], [8, 'maj7', 4], [10, 'add9', 4], [0, 'm9', 4]],                         // i7 ♭VImaj7 ♭VII i9
      h3: [[0, 'm9', 4], [10, 'add9', 4], [8, 'maj9', 8]],                                       // i9 ♭VII ♭VImaj9
      h4: [[0, 'm11', 8], [3, 'maj9', 4], [10, '7sus4', 4]],                                     // i11 ♭IIImaj9 ♭VII7sus4
      h5: [[5, 'm9', 4], [10, '7sus4', 4], [3, 'maj9', 8]],                                      // iv9 ♭VII7sus4 ♭IIImaj9
      h6: [[0, 'm11', 4], [8, 'maj9', 4], [5, 'm9', 4], [7, 'm7', 4]],                           // i11 ♭VImaj9 iv9 v7
    },
  },
  turnarounds: { major: [[2, 'm9', 2], [7, '9sus4', 2]], minor: [[8, 'maj9', 2], [10, 'add9', 2]] },   // ii9|V9sus4 · ♭VI|♭VII
  // grids: the kick on every beat, the clap on 2 and 4, open hats on the off-beats; W is the ride (this kit plays the block lane)
  grids: {
    deep:   { K: 'x...x...x...x...', C: '....x.......x...', H: '..o...o...o...o.' },
    soul:   { K: 'x...x...x...x...', C: '....x.......x...', S: '....s.......s...', H: '..o...o...o...o.', R: '...s......s..s..' },
    shaker: { K: 'x...x...x...x...', C: '....x.......x...', H: '..o...o...o...o.', X: 'xsasxsasxsasxsas' },
    drive:  { K: 'x...x...x...x...', C: '....x.......x...', S: '....s.......s...', H: '..o...o...o...o.', X: 'xsasxsasxsasxsas', R: '.......s......s.' },
    ride:   { K: 'x...x...x...x...', C: '....x.......x...', H: '..o.......o.....', W: 'x.s.x.s.x.s.x.s.' },
    break:  { C: '....x.......x...', H: '..o...o...o...o.', X: 's.s.s.s.s.s.s.s.' },
  },
  fills: {
    C1: { roll: [[12, 0.55], [13, 0.4], [14, 0.65], [15, 0.8]], voice: 'clap' },                // clap roll
    C2: { roll: [[8, 0.35], [10, 0.45], [11, 0.4], [12, 0.55], [13, 0.5], [14, 0.7], [15, 0.85]], voice: 'clap' },
    K1: { roll: [], voice: 'kick' },                                                             // kick drop on beat 4
    S1: { roll: [[12, 0.5], [13, 0.3], [14, 0.45], [15, 0.6]] },                                 // soft snare roll
    X1: { stop: 14 },                                                                             // everything drops for an 8th
  },
  // comping cells [step, length, vel]: held pads, and organ stabs on the off-beats and syncopations (one carries the loop)
  comps: {
    pad:    [[0, 16, 0.85]],
    breath: [[0, 10, 0.85], [10, 6, 0.7]],
    sparse: [[3, 3, 0.85], [11, 5, 0.8]],
    tres:   [[0, 3, 0.9], [3, 3, 0.8], [6, 4, 0.85]],
    stab:   [[3, 2, 0.85], [6, 2, 0.9], [10, 2, 0.85], [14, 2, 0.8]],
    off:    [[2, 2, 0.85], [6, 2, 0.9], [10, 2, 0.85], [14, 2, 0.9]],
  },
  keysLevel: 1.75,                                                   // Chords only: +4.9 dB (the organ alone sits 5-7 dB under the band)
  plan(plan, { r, energy }) {
    const e = ENERGY[energy] ?? 1, pick = (a, b, d = 3) => +(a + r() * (b - a)).toFixed(d), sd = 60 / plan.bpm / 4;
    const loop = pickW(r, plan.ep.comp);                             // house repeats a stab pattern: one cell's weight ×4
    plan.ep.comp[loop] = +(plan.ep.comp[loop] * 4 + 0.5).toFixed(2);
    const cells = [pickW(r, BASS_POOL[e]), pickW(r, BASS_POOL[e])];
    plan.house = {
      chords: {
        detune: pick(4, 8, 1), padDetune: pick(8, 14, 1), atk: pick(...[[0.06, 0.14], [0.04, 0.09], [0.025, 0.06]][e]), rel: pick(0.22, 0.4),
        q: pick(0.9, 1.6, 2), pluck: pick(...[[0.6, 1.2], [0.9, 1.7], [1.2, 2.2]][e], 2), pluckTau: pick(0.07, 0.12), echo: pick(0.14, 0.24, 2),
        open: pick(1.35, 1.7, 2), build: pick(1.15, 1.35, 2),
      },
      // the sidechain: chords −3…−6 dB and bass −2.5…−4 dB on each kick, back over about an 8th (τ .28-.38 of it)
      pump: { chordDb: pick(...[[3, 4.5], [3.8, 5.2], [4.5, 6]][e], 2), bassDb: pick(2.5, 4, 2), atk: 0.004, hold: 0.02, rec: +(pick(0.28, 0.38) * 2 * sd).toFixed(4) },
      bass: { cells, approach: [0.3, 0.4, 0.45][e], tri: pick(0.22, 0.4, 2), drive: pick(1.3, 2.2, 2), lp: pick(520, 820, 0) },
      lead: { vib: pick(10, 16, 1), glide: pick(0.03, 0.05) },
    };
    Object.assign(plan.drums.kit, {
      kickF0: pick(115, 140, 1), kickF1: pick(47, 54, 1), kickSweep: pick(0.035, 0.05), kickDecay: pick(...[[0.17, 0.22], [0.15, 0.2], [0.13, 0.18]][e]),
      clapF: pick(1000, 1400, 0), clapTail: pick(0.07, 0.11), snareDecay: pick(0.055, 0.08), hatHP: pick(7000, 8800, 0), hatClosed: pick(0.012, 0.018),
      openDecay: pick(0.14, 0.22), shakerDecay: pick(0.026, 0.038), rideDecay: pick(0.28, 0.42),
    });
    // the lead: 'soft' → the vox chop, else the pluck; its motif cut to 2-4 short notes, so it repeats like a sample
    const n = 2 + Math.floor(r() * 3), last = 3 + Math.floor(r() * 4);
    if (plan.lead) {
      const m = plan.lead.motif, k = Math.min(n, m.steps.length);
      plan.lead.voice = plan.lead.timbre === 'soft' ? 'vox' : 'pluck';
      plan.lead.motif = { ...m, steps: m.steps.slice(0, k), durs: m.durs.slice(0, k).map((d, i) => (i < k - 1 ? Math.min(d, 3) : last)),
        contour: m.contour.slice(0, k), vel: m.vel.slice(0, k) };
    }
  },
  /** Deep voicing: the cheapest shape of the chord's quality by Σ|Δ| + 1.5·max(0, |Δtop| − 3) + .35·|mean − 62| (the first
   *  shape −.5), lowest note MIDI 48-64, top ≤ 79. A track's first chord weighs .7·|mean − 62 − plan.register| instead
   *  (issue #31: consecutive tracks don't open on the same notes). A chord that can keep its voicing keeps it. */
  voicing(sp, prev, c, plan) {
    const shapes = SHAPES[c.q];
    if (!shapes) return rootless(sp, prev);
    const home = 62 + (prev ? 0 : (plan.register || 0)), w = prev ? 0.35 : 0.7;
    let best = null, bc = Infinity;
    shapes.forEach((sh, k) => {
      for (let o = 2; o <= 6; o++) {
        const v = sh.map(i => sp.root + i + 12 * o);
        if (v[0] < 48 || v[0] > 64 || v[3] > 79) continue;
        let cost = w * Math.abs(mean(v) - home) - (k ? 0 : 0.5);
        if (prev && prev.length === 4) { for (let i = 0; i < 4; i++) cost += Math.abs(v[i] - prev[i]); cost += 1.5 * Math.max(0, Math.abs(v[3] - prev[3]) - 3); }
        if (cost < bc) { bc = cost; best = v; }
      }
    });
    return best || rootless(sp, prev);
  },
  /** One bar of rolling off-beat sub (the main groove only; intro, break and outro keep the default held roots). */
  bass({ plan, st, b, mode, chords, nextChords, chordAt, stop }) {
    if (mode !== 'kick') return null;
    const H = plan.house.bass, r = st.rS;
    const id = b.j % 4 === 3 ? H.cells[r() < 0.6 ? 1 : 0] : H.cells[r() < 0.85 ? 0 : 1];
    let hits = [...BASS_CELLS[id]].map((c, s) => [s, c]).filter(([, c]) => c !== '.');
    const last = chords[chords.length - 1], nx = nextChords && nextChords[0];
    if (nx && nx.root !== last.root && r() < H.approach) { hits = hits.filter(([s]) => s < 14); hits.push([14, 'A']); }   // approach the next root
    if (stop != null) hits = hits.filter(([s]) => s < stop);
    const end = stop ?? 16;
    return hits.map(([s, c], k) => {
      const root = bassNote(mod12(plan.key.tonic + chordAt(s).root), st.prevBass), kick = 4 * Math.floor(s / 4) + 4;
      let midi = root, len = Math.min(k + 1 < hits.length ? hits[k + 1][0] : end, kick) - s, vel = s % 4 === 2 ? 0.88 : 0.72, slide = false;
      if (c === 'o') { midi = root + 12; len *= 0.8; vel = 0.78; slide = r() < 0.12; }
      else if (c === 'A') {
        const tg = bassNote(mod12(plan.key.tonic + nx.root), root), u = r();
        midi = u < 0.45 ? tg - 1 : u < 0.7 ? tg + 1 : tg - 5; vel = 0.8; slide = r() < 0.2;
      } else {
        if (c === '5') midi = root + 7 <= 50 ? root + 7 : root - 5;
        len *= plan.bass.factor;
        st.prevBass = root;
      }
      return { s, midi, vel, dur: Math.max(0.5, len), slide };
    });
  },
  voices(ac, dry, wet, plan, ctx) {
    const o = { registry: ctx.registry, tag: ctx.tag, t0: ctx.t0 }, pump = createPump(ac, plan, { ...o, stepDur: ctx.stepDur });
    return {
      ep: createHouseChords(ac, dry, wet, plan.house.chords, plan.ep.lp, pump, { ...o, cap: ctx.cap, r: ctx.r, log: ctx.log, stepDur: ctx.stepDur }),
      bass: createHouseBass(ac, dry, plan.house.bass, pump, o),
      kit: createHouseKit(ac, dry, wet, plan.drums.kit, pump, o),
      lead: plan.lead ? createHouseLead(ac, dry, wet, plan.lead, plan.house.lead, { ...o, stepDur: ctx.stepDur, r: ctx.r }) : null,
    };
  },
};
